"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Link } from "@/i18n/navigation";
import { trackFaqOpen } from "@/lib/analytics";
import { INLINE_LINK } from "@/lib/inline-links";
import { cn } from "@/lib/utils";

type Item = { q: string; a: string };

// An answer may carry internal links written as [ankertekst](/pad). Anything
// else in the string stays plain text - this is deliberately not a markdown
// renderer, it only exists so an answer can link on its own words.
function answerNodes(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const match of text.matchAll(INLINE_LINK)) {
    const start = match.index ?? 0;
    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <Link
        key={start}
        href={match[2]}
        className="font-medium text-brand underline underline-offset-4 transition-colors hover:text-brand-dark"
      >
        {match[1]}
      </Link>,
    );
    cursor = start + match[0].length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function AccordionList({
  items,
  headingLevel = "h3",
}: {
  items: Item[];
  headingLevel?: "h2" | "h3";
}) {
  const [open, setOpen] = useState<number | null>(null);
  const Heading = headingLevel;

  return (
    <div className="divide-y divide-line">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q}>
            <Heading>
              <button
                onClick={() => {
                  setOpen(isOpen ? null : i);
                  if (!isOpen) trackFaqOpen(i, item.q);
                }}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
              >
                <span className="text-[16px] font-semibold text-navy">{item.q}</span>
                <ChevronDown
                  className={cn(
                    "size-5 shrink-0 text-brand transition-transform duration-300",
                    isOpen && "rotate-180",
                  )}
                />
              </button>
            </Heading>
            <div
              className={cn(
                "grid overflow-hidden transition-all duration-300 ease-out",
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                <p className="pb-5 text-[15px] leading-relaxed text-slate">
                  {answerNodes(item.a)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Faq({ hideHeading = false }: { hideHeading?: boolean }) {
  const t = useTranslations("Faq");
  const items = t.raw("items") as Item[];

  if (hideHeading) {
    return (
      <section className="py-16 lg:py-24" id="faq" data-track-section="faq">
        <Container className="max-w-3xl">
          <AccordionList items={items} headingLevel="h2" />
        </Container>
      </section>
    );
  }

  return (
    <section className="py-12 lg:py-20" id="faq" data-track-section="faq">
      <Container>
        <div className="rounded-[1.75rem] bg-mist px-6 py-12 sm:px-10 lg:px-16 lg:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <h2 className="text-balance text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl">
                {t("title")}
              </h2>
              <p className="mt-5 text-[15px] text-slate">
                {t("more")}{" "}
                <Link
                  href="/klantenservice"
                  className="font-medium text-brand underline underline-offset-4 transition-colors hover:text-brand-dark"
                >
                  {t("moreLink")}
                </Link>
              </p>
            </div>
            <AccordionList items={items} />
          </div>
        </div>
      </Container>
    </section>
  );
}
